// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartlineContainerStyles } from "@sc/App/Components/Cart/CartLineContainer";
import { color, getSystemColor, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

cartlineContainerStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: 102px 25px 2.1fr 1.2fr 0.9fr 1.3fr 15px;
        grid-column-gap: 13px;
        background: ${color("common.background")};
        padding: 16px 12px;
        width: 100%;
        border-bottom: 1px solid ${color("common.border")};

        &:last-child {
            border: none;
        }

        ${xScreen("lte", "lg")} {
            grid-template-columns: 102px 25px 3fr 1.5fr 0.9fr 1.3fr 15px;
        }
        ${xScreen("lte", "md")} {
            padding: 16px 0;
            grid-template-columns: 91px 25px 3fr 1.5fr 0.9fr 1.3fr 15px;
        }

        @media print {
            width: 100%;
            content: "";
            display: table;
            clear: both;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            border-bottom: 1px solid ${getSystemColor("common.border")};
        }
    `,
};
cartlineContainerStyles.image = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 5;
        ${xScreen("lte", "md")} {
            grid-row: 1 / 3;
            margin-bottom: 13px;
        }
        @media print {
            min-width: 120px;
            min-height: 120px;
            margin-bottom: 0;
            float: left;
        }
    `,
};
cartlineContainerStyles.description = {
    css: css`
        grid-column: 2 / 4;
        grid-row: 1 / 2;
        position: relative;
        display: flex;
        flex-direction: column;
        padding-right: 20px;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 7;
            grid-row: 1 / 3;
            padding-right: 0;
        }
        @media print {
            min-width: 75%;
            max-width: 75%;
            min-height: 120px;
            float: right;
        }
    `,
};
cartlineContainerStyles.price = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        position: relative;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 3 / 4;
            padding-bottom: 8px;
        }

        @media print {
            min-width: 35%;
            max-width: 35%;
            min-height: 50px;
            float: left;
        }
    `,
};
cartlineContainerStyles.qty = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 4;
            grid-row: 4 / 5;
            margin-right: 16px;
        }
        ${xScreen("lte", "xs")} {
            grid-column: 1 / 3;
            grid-row: 4 / 5;
            padding-bottom: 5px;
        }
        @media print {
            margin-right: 0;
            min-width: 30%;
            min-height: 50px;
            float: left;
        }
    `,
};
cartlineContainerStyles.total = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 1 / 2;
        position: relative;
        ${xScreen("lte", "md")} {
            grid-column: 4 / 8;
            grid-row: 4 / 5;
        }
        ${xScreen("lte", "xs")} {
            grid-column: 3 / 7;
            grid-row: 4 / 5;
        }
        @media print {
            min-width: 35%;
            min-height: 50px;
            float: right;
        }
    `,
};
cartlineContainerStyles.remove = {
    css: css`
        grid-column: 7 / 8;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-row: 1 / 6;
        }
        ${xScreen("lte", "xs")} {
            grid-row: 1 / 7;
        }
    `,
};
cartlineContainerStyles.actions = {
    css: css`
        grid-column: 2 / 4;
        grid-row: 2 / 3;
        position: relative;
        display: flex;
        justify-content: space-between;
        padding-right: 44px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 5 / 6;
            padding-top: 10px;
            padding-right: 0;
        }
        ${xScreen("lte", "xs")} {
            grid-row: 5 / 6;
        }
        @media print {
            display: none;
        }
    `,
};
cartlineContainerStyles.notes = {
    css: css`
        grid-column: 2 / 8;
        grid-row: 4 / 5;
        position: relative;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 6 / 7;
        }
        ${xScreen("lte", "xs")} {
            grid-row: 6 / 7;
        }
        @media print {
            min-width: 100%;
            float: left;
        }
    `,
};
